/**
 * /dashboard/accounting/accounts — browsable Chart of Accounts with
 * balances (Sprint 4 §A).
 *
 * Renders every account in the per-tenant chart grouped by type, each
 * with its running ledger balance (from GET
 * /accounting/accounts-with-balances). A "Show archived" toggle widens
 * the result. System accounts carry a "System" badge.
 *
 * Custom-account CRUD (add / edit / archive) lives on the dedicated
 * /accounts/new form + the inline edit affordances; the "Add account"
 * action links there.
 *
 * Role-gate: accountingRole = company_admin | hr_manager | accountant.
 * Feature-gate: RequireFeature("accounting") — enforced by the backend;
 * the frontend wraps the page in <AccountingFeatureGate>.
 */

import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { AccountingModuleShell } from "@/components/shared/accounting-module-shell";
import { ChartOfAccounts } from "@/components/accounting/chart-of-accounts";
import { AccountingFeatureGate } from "@/components/accounting/accounting-feature-gate";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/accounting/accounts/",
)({
  component: AccountsListRoute,
});

function AccountsListRoute() {
  const { tenantSlug } = useParams({ strict: false }) as {
    tenantSlug: string;
  };
  return (
    <AccountingFeatureGate>
      <AccountingModuleShell
        title="Chart of Accounts"
        description="Every ledger account with its current balance. System accounts are seeded at tenant creation; add custom sub-accounts to extend the chart."
        actions={
          <Button asChild size="sm" className="gap-1.5">
            <Link
              to="/app/$tenantSlug/accounting/accounts/new"
              params={{ tenantSlug }}
            >
              <Plus aria-hidden className="h-4 w-4" />
              Add Account
            </Link>
          </Button>
        }
      >
        <ChartOfAccounts />
      </AccountingModuleShell>
    </AccountingFeatureGate>
  );
}
